/**
 * THE JOB CHIP'S FACTS (PR-2b, 2026-09-28; design record
 * docs/PLAN-browser-signed-in-apps-honesty-20260926.md §3.4).
 *
 * A reply that touched a job the person is waiting on carries the job's facts, and the chat draws
 * one line above it: "Organise the last 25 faxes · 0 of 25 faxes filed · waiting for the eFax
 * sign-in". The server sends FACTS, never words (functions/src/prest0nSession/threadJobs.ts
 * `jobChipFacts`, the twin this reader must accept); the chat composes the line from its own
 * registered copy (jobStatusCopy.ts).
 *
 * This is the one reader of those facts on the client, for every path a reply arrives by (the
 * live stream's done event, the task poll, a reload's resume, the stored message). It imports
 * nothing, so it can ride any of them. A field it cannot read drops that job, never the reply.
 */

export type JobChipState = 'working' | 'waiting' | 'done' | 'stopped';

export interface JobChipFact {
  jobId: string;
  title: string;
  done: number;
  count: number | null;
  unit: string;
  outcome: string;
  state: JobChipState;
  waitingFor: { app: string; lane: 'sign-in' | 'connection' } | null;
}

/** The server sends at most three (a thread holds no more active jobs). */
export const MAX_JOB_CHIPS = 3;

const STATES: ReadonlySet<string> = new Set(['working', 'waiting', 'done', 'stopped']);

function text(v: unknown, max: number): string {
  return typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '';
}

function whole(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;
}

/** PURE. The chip facts a reply carries, or undefined when it carries none it can read. */
export function readJobStatus(raw: unknown): JobChipFact[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: JobChipFact[] = [];
  for (const item of raw.slice(0, MAX_JOB_CHIPS)) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const jobId = text(r.jobId, 80);
    const title = text(r.title, 120);
    const state = text(r.state, 20);
    const done = whole(r.done);
    if (!jobId || !title || !STATES.has(state) || done === null) continue;
    const count = r.count === null || r.count === undefined ? null : whole(r.count);
    if (r.count !== null && r.count !== undefined && (count === null || count === 0)) continue;
    const w = r.waitingFor && typeof r.waitingFor === 'object' ? (r.waitingFor as Record<string, unknown>) : null;
    const app = w ? text(w.app, 80) : '';
    const lane = w && w.lane === 'connection' ? 'connection' : 'sign-in';
    out.push({
      jobId,
      title,
      done,
      count,
      unit: text(r.unit, 40),
      outcome: text(r.outcome, 120),
      state: state as JobChipState,
      waitingFor: state === 'waiting' && app ? { app, lane } : null,
    });
  }
  return out.length > 0 ? out : undefined;
}
